import React, { useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { Box, Inline } from '@styleseat/ui';
import { BrandColor, colors } from '@styleseat/brand';
import Image from '../../../shared/Image';
import thunderIcon from '../../../../../images/icons/icon-thunder.svg';
import analytics from '../../../../modules/analytics';
import '../Notification.scss';
import RatingStars from '../../../RatingStars';
import {
  BodyText,
  Button,
  HeaderText,
} from '../../../ui';
import { getFromNowDisplay } from '../../../../modules/provider/Notifications/Notifications';
import useMount from '../../../../hooks/useMount';
import { getShareProviderCallback } from '../../../shared/providerProfile/reviews/ProviderReviewBuild';
import {
  PROFILE_EDIT_ROUTE,
  PRO_SERVICE_MENU,
  PRO_PROMOTE_PROFILE,
} from '../../../../route-names';

export const PROFILE_FEEDBACK_NOTIFICATION_PROMOTE_EVENT = 'profile_feedback_notification_promote_on_instagram_clicked';
export const PROFILE_FEEDBACK_NOTIFICATION_VIEWED_EVENT = 'profile_feedback_notification_promote_on_instagram_viewed';
export const PROFILE_FEEDBACK_NOTIFICATION_SERVICES_CHOICE = 0;
export const PROFILE_FEEDBACK_NOTIFICATION_PHOTOS_CHOICE = 1;
export const PROFILE_FEEDBACK_NOTIFICATION_REVIEWS_CHOICE = 2;
/**
 * The Profile Feedback notification
 */
export const ProfileFeedbackNotificationBase = ({
  user,
  route,
  linkTo,
  notification,
}) => {
  const {
    stars, name, message,
    choice_selected: choiceSelected,
    creation_time: creationTime,
    provider_id: providerId,
  } = notification;

  const [expanded, setExpanded] = useState(false);

  const highReview = stars >= 4;
  const longAgo = getFromNowDisplay(creationTime);
  const displayName = name === 'Anonymous' ? 'A client' : name;
  let header = 'Your profile is getting lots of love from clients on StyleSeat. Promote yourself on Instagram to share the news!';
  let ctaButtonText = 'Share on Instagram';
  let linkToRoute = PRO_PROMOTE_PROFILE;
  let linkToParams = {
    providerId,
    source: 'profile_feedback',
  };
  let constructiveFeedback;

  const trackingParams = {
    pro_id: providerId,
    source: route.params?.notificationSource,
    name,
    stars,
    message,
    choiceSelected,
    creation_time: creationTime,
  };

  if (!highReview) {
    header = `${displayName} reviewed your profile.`;

    switch (choiceSelected) {
      case PROFILE_FEEDBACK_NOTIFICATION_SERVICES_CHOICE:
        constructiveFeedback = '"Could you add more detailed descriptions of your services, so I know what to book?"';
        ctaButtonText = 'Edit Services';
        linkToRoute = PRO_SERVICE_MENU;
        trackingParams.type = 'editServices';
        break;
      case PROFILE_FEEDBACK_NOTIFICATION_PHOTOS_CHOICE:
        constructiveFeedback = '"I want to see more photos of your work before I book."';
        ctaButtonText = 'Add Photos';
        linkToRoute = PROFILE_EDIT_ROUTE;
        linkToParams = {
          ...linkToParams,
          modalId: 'add-gallery-photo',
        };
        trackingParams.type = 'addPhotos';
        break;
      case PROFILE_FEEDBACK_NOTIFICATION_REVIEWS_CHOICE:
        constructiveFeedback = '"I want to see more reviews on your profile before I book."';
        ctaButtonText = 'Invite Clients To Review You';
        trackingParams.type = 'requestReviews';
        break;
      default:
        break;
    }
  }

  const textToRender = expanded ? message : `${message.substr(0, 35 - 3)}${message.length >= 35 ? '...' : ''}`;

  const handleCTAClick = async () => {
    if (highReview) {
      analytics.track(PROFILE_FEEDBACK_NOTIFICATION_PROMOTE_EVENT, trackingParams);
    } else if (choiceSelected === PROFILE_FEEDBACK_NOTIFICATION_SERVICES_CHOICE) {
      analytics.track('pro_profile_feedback_notification_constructive_edit_services_click', trackingParams);
    } else if (choiceSelected === PROFILE_FEEDBACK_NOTIFICATION_PHOTOS_CHOICE) {
      analytics.track('pro_profile_feedback_notification_constructive_add_photos_click', trackingParams);
    } else if (choiceSelected === PROFILE_FEEDBACK_NOTIFICATION_REVIEWS_CHOICE) {
      analytics.track('pro_profile_feedback_notification_constructive_request_reviews_click', trackingParams);
      const doShare = await getShareProviderCallback(user.providerId, user.email);
      return doShare();
    }

    return linkTo(linkToRoute, linkToParams);
  };

  useMount(() => {
    if (highReview) {
      analytics.track(PROFILE_FEEDBACK_NOTIFICATION_VIEWED_EVENT, trackingParams);
    } else {
      analytics.track('pro_profile_feedback_notification_constructive_view', trackingParams);
    }
  });

  return (
    <TouchableOpacity
      type="button"
      className="button-notification"
      onPress={() => setExpanded(!expanded)}
      activeOpacity={1}
    >
      <section className="notification">
        <div className="icon">
          <Image
            source={{
              uri: thunderIcon,
              width: 32,
              height: 40,
              resizeMode: 'center',
            }}
          />
        </div>
        <section className="notification-body short-block">
          <section className="notification-text">
            <HeaderText
              level={4}
            >
              {header}
            </HeaderText>
            {!highReview && (
              <Box
                paddingTop={4}
                paddingBottom={12}
              >
                <BodyText
                  color={colors.ash}
                  appearance="smaller"
                  textTransform="capitalize"
                >
                  {longAgo}
                </BodyText>
              </Box>
            )}
            <Inline
              space={12}
              paddingTop={highReview ? 12 : 0}
            >
              <Box
                minWidth={4}
                color={BrandColor.Blue}
                borderRadius={4}
              />
              {highReview ? (
                <Box>
                  <BodyText
                    weight="bold"
                    appearance="medium"
                  >
                    {`${displayName} ${message ? 'left a review' : 'rated your profile'}`}
                  </BodyText>
                  {message && (
                    <BodyText
                      appearance="medium"
                    >
                      {`"${textToRender}"`}
                    </BodyText>
                  )}
                </Box>
              ) : (
                <BodyText
                  appearance="medium"
                >
                  {constructiveFeedback}
                </BodyText>
              )}
            </Inline>

          </section>
          {highReview && (
            <Inline
              paddingBottom={4}
              paddingLeft={8}
              alignItems="center"
              paddingTop={8}
            >
              <RatingStars
                style={{ width: '10%' }}
                ratingStars={stars}
                size={14}
              />
              <BodyText
                color={colors.ash}
              >
                &bull;
              </BodyText>
              <BodyText
                color={colors.ash}
                textTransform="capitalize"
                appearance="smaller"
              >
                &nbsp;
                {longAgo}
              </BodyText>
            </Inline>
          )}
          <Box
            paddingTop={highReview ? 8 : 16}
            alignItems="start"
          >
            <Button
              appearance="text-light"
              size="small"
              onClick={handleCTAClick}
              text={ctaButtonText}
              stopPropagation
              testID="profile-feedback-notification-cta-button"
            />
          </Box>
        </section>
      </section>
    </TouchableOpacity>
  );
};

ProfileFeedbackNotificationBase.propTypes = {
  /** The notification object */
  notification: PropTypes.object.isRequired,
  linkTo: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  route: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  user: state.user,
  route: state.route,
});

const mapDispatchToProps = dispatch => ({
  linkTo: (route, params, notify = true) => dispatch.route.go({
    route,
    params,
    notify,
  }),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProfileFeedbackNotificationBase);
